import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';

import {
	type MessageTemplateContext,
	type RecommendedApp,
	type ActionTier,
} from '@post-office/message-template--twc-sidenav-banner';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';
import { type ExperimentArm } from './hydrate';

export type HydrationResult = {
	product: MessageTemplateContext['product'];
	redirectTo: 'jira' | 'confluence';
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	arm: ExperimentArm;
	recommendedApp?: RecommendedApp;
	actionTier?: ActionTier;
	experimentKey?: string;
	targetTenantId?: string;
	collectionEntitlementId?: string;
};

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

export type HydrateMessageContext = MessageTemplateContext & {
	messageInstanceId: string;
	eventTime: string;
};

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <
	U extends (
		renderProps: Omit<HydrationResult, 'postOffice'> & {
			onDismissed: () => void;
		},
	) => JSX.Element | null,
>(
	render: U,
) => (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: HydrateMessageContext,
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: HydrateMessageContext,
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: HydrateMessageContext,
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: Omit<
			Omit<HydrationResult, 'postOffice'> & {
				onDismissed: () => void;
			},
			keyof HydrationResult
		>;
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: HydrateMessageContext,
) => Omit<HydrationResult, 'postOffice'> & {
	onDismissed: () => void;
} = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: {
			product: 'jira' | 'confluence' | 'jira-confluence';
			cloudId: string;
			orgId?: string;
			twcEntitlementId?: string;
			redirectTo?: 'jira' | 'confluence';
		};
	}) => JiraSideNavMessageMetadata,
) => (params: {
	messageContext: {
		product: 'jira' | 'confluence' | 'jira-confluence';
		cloudId: string;
		orgId?: string;
		twcEntitlementId?: string;
		redirectTo?: 'jira' | 'confluence';
	};
}) => TransformMetadataResult = serverPipeline.createTransformMetadata;
